import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ROUTER_NAME } from '../router'
import Course from '../../api/v2/models/course'
import Review from '../../api/v2/models/review'
import CourseLesson from '../../api/v2/models/course_lesson'
import Material from '../../api/v2/models/material'
import Banner from '../../api/v2/models/banner'
import Chapter from '../../api/v2/models/chapter'
import api from '../../api/v2/api'
import trackingLesson from '../trackings/lesson'
import { LearningMeterController } from '../learning/learningMeterController'
import { bindLearningEventBridge } from '../learning/learningEventBridge'
import { userState } from '../stores/user'
import { checkMobile } from '../utils/utils'
import { headerState } from '../stores/header'
import markdownParser from '../../../helpers/markdown-parser'
import { Commentable } from '../../api/v2/models/comment'
import { format } from 'date-fns'
import VoteForm from '../../api/v2/forms/vote'
import { makeChannel, removeChannel } from '../../../action_cable/cable'
import { getQueryString, getQueryNumber } from '../../../helpers/route-helper'

interface LeftBarItem {
  id: number
  type: string
  icon: string
  image: string
  title: string
  action: () => void
  chapterId?: number | null
}

export function useCourseMyDetail() {
  const route = useRoute()
  const router = useRouter()

  const mobile = ref(checkMobile())
  const course = ref<Course | null>(null)
  const courseLessons = ref<CourseLesson[]>([])
  const currentLesson = ref<CourseLesson | null>(null)
  const courseMaterials = ref<Material[]>([])
  const lessonMaterials = ref<Material[]>([])
  const completedLessonCount = ref(0)
  const banners = ref<Banner[]>([])
  const review = ref<Review | null>(null)
  const chapters = ref<Chapter[]>([])
  const isMobile = ref(false)
  const leftDrawer = ref(!checkMobile())
  const rightDrawer = ref(false)
  const disabledNextBtn = ref(false)
  const disabledPreviousBtn = ref(false)
  const chatContent = ref("")
  const tab = ref<string | null>(null)
  const channel = ref<any>(null)
  const currentLessonQuestionsCount = ref(0)
  const mobileViewMode = ref<'lesson' | 'question' | 'material'>('lesson')
  const leftBarItems = ref<LeftBarItem[]>([])
  const lazyLoading = ref(true)
  const descriptionShowAll = ref(false)
  const isProcessingFinish = ref(false)
  const isProcessingRevert = ref(false)

  const user = computed(() => userState.user)
  const schoolSetting = computed(() => headerState.schoolSetting)

  // New learning-time meter (dual write, text/video lessons, flag-gated). Runs
  // alongside the legacy trackingLesson without replacing it.
  const learningMeter = new LearningMeterController()
  const syncLearningMeter = () => {
    const started = learningMeter.sync({
      enabled: user.value?.learningMeterEnabled ?? false,
      diagRate: user.value?.learningMeterDiagRate ?? 0,
      lessonId: currentLesson.value?.lesson?.id ?? 0,
      lessonType: currentLesson.value?.lesson?.lessonType ?? ''
    })
    // Opening an already-completed lesson again -> lesson_reopened (spec task 2-3b).
    if (started && currentLesson.value?.isLearnt) {
      learningMeter.emitLessonReopened()
    }
  }
  watch([currentLesson, user], syncLearningMeter)
  onBeforeUnmount(() => learningMeter.dispose())

  // Relays the exam iframe's "answer saved" signal into the meter session
  // for the exam lesson currently open (task 2-3, "テスト回答シグナル").
  const detachLearningEventBridge = bindLearningEventBridge((eventType) => {
    if (eventType === 'assessment_answered') learningMeter.emitAssessmentAnswered()
  })
  onBeforeUnmount(() => detachLearningEventBridge())

  const onVideoPlay = () => learningMeter.setMediaPlaying(true)
  const onVideoPause = () => learningMeter.setMediaPlaying(false)
  const onVideoMilestone = (milestone: number) => learningMeter.emitVideoMilestone(milestone)

  const lessonIds = computed(() => {
    const dbLessonIds = courseLessons.value.map((cl) => cl.lesson.id)
    let ids: number[] = []
    if (chapters.value.length > 0) {
      chapters.value.forEach(chapter => {
        const chapterLessonIds = dbLessonIds.filter(id => chapter.lessonIds.includes(id))
        ids = [...ids, ...chapterLessonIds]
      })
    } else {
      ids = dbLessonIds
    }
    return ids
  })

  const headerNavColor = computed(() => schoolSetting.value?.navColor || '#1976d2')
  const headerNavTextColor = computed(() => schoolSetting.value?.navTextColor)

  const proccessCount = computed(() => {
    if (courseLessons.value.length === 0) return '--'
    return ` ${Math.round(completedLessonCount.value / courseLessons.value.length * 100)}%(${completedLessonCount.value}/${courseLessons.value.length})`
  })

  const hamburgerColor = computed(() => schoolSetting.value ? `color: ${schoolSetting.value.hamburgerColor}` : "color: white")

  const breadcrumbs = computed(() => [
    { text: 'マイコース一覧', disabled: false, exact: true, to: '/classroom/my-courses/' },
    { text: currentLesson.value?.lesson?.name || '', disabled: true }
  ])

  const lessonIdQuery = computed(() => getQueryString(route.query, 'lesson_id'))
  const lessonIdQueryNumber = computed(() => getQueryNumber(route.query, 'lesson_id'))
  const tabQuery = computed(() => getQueryString(route.query, 'tab'))

  const currentLessonId = computed(() => String(lessonIdQuery.value || currentLesson.value?.lesson?.id || ''))

  const leftBarSelectedItem = computed(() => {
    if (lessonIdQuery.value) {
      return leftBarItems.value.findIndex((item) => item.id == lessonIdQueryNumber.value)
    }
    return -1
  })

  const headerTitle = computed(() => currentLesson.value?.lesson?.name || '')

  const commentable = computed<Commentable>(() => ({
    commentableId: currentLesson.value?.lesson?.id || 0,
    commentableType: "Lesson"
  }))

  const openOtherTab = () => {
    if (document.hidden) {
      trackingLesson.stop()
    } else {
      if (currentLesson.value && user.value) {
        trackingLesson.start(user.value.currentSchoolId, currentLesson.value.lesson.id)
      }
    }
  }

  const setLeftBarMenu = (chaptersList: Chapter[], courseLessonsList: CourseLesson[]) => {
    let items: LeftBarItem[] = [{
      id: course.value!.id,
      type: 'header',
      icon: '',
      image: course.value!.image?.url || '',
      title: course.value!.name,
      chapterId: null,
      action: () => router.push({ name: ROUTER_NAME.MY_COURSES })
    }]
    if (chaptersList.length > 0) {
      chaptersList.forEach((item) => {
        const chapterLessons = courseLessonsList.filter((cl) => item.lessonIds.indexOf(cl.lesson.id) !== -1)
        chapterLessons.forEach((courseLesson) => {
          items.push({
            id: courseLesson.lesson.id,
            type: 'item',
            icon: courseLesson.isLearnt ? 'mdi-check' : '',
            image: '',
            title: `${!courseLesson.lesson.publishedAt ? '<span class="bg-danger p-1">非公開</span>' : ''}${courseLesson.lesson.name}`,
            chapterId: item.id,
            action: () => goLesson(courseLesson.lesson.id)
          })
        })
      })
    } else {
      items = items.concat(
        courseLessonsList.map((item) => ({
          id: item.lesson.id,
          type: 'item',
          icon: item.isLearnt ? 'mdi-check' : '',
          image: '',
          title: `${!item.lesson.publishedAt ? '<span class="bg-danger p-1">非公開</span>' : ''}${item.lesson.name}`,
          chapterId: null,
          action: () => goLesson(item.lesson.id)
        }))
      )
    }
    leftBarItems.value = items
  }

  const goNextLesson = () => {
    if (!currentLesson.value) return
    if (lessonIds.value.indexOf(currentLesson.value.lesson.id) == lessonIds.value.length - 1) {
      disabledNextBtn.value = true
      return
    }
    const nextLessionIndex = lessonIds.value.indexOf(currentLesson.value.lesson.id) + 1
    goLesson(lessonIds.value[nextLessionIndex])
  }

  const previousLession = () => {
    if (!currentLesson.value) return
    if (lessonIds.value.indexOf(currentLesson.value.lesson.id) == 0) return
    const previousLessionIndex = lessonIds.value.indexOf(currentLesson.value.lesson.id) - 1
    goLesson(lessonIds.value[previousLessionIndex])
  }

  const getBanner = (position: string): Banner | undefined => {
    return banners.value.find(banner => banner.position == position)
  }

  const goLesson = (lessonId: number) => {
    if (lessonIdQuery.value == lessonId.toString()) return
    descriptionShowAll.value = false
    router.push({
      name: ROUTER_NAME.MY_COURSE_DETAIL,
      query: { lesson_id: lessonId.toString() }
    })
    mobileViewMode.value = 'lesson'
  }

  const downloadFile = (link: string) => {
    window.open(`${location.origin}/${link}`)
  }

  const showRightMenu = () => {
    rightDrawer.value = !rightDrawer.value
  }

  const toggleLeftDrawer = () => {
    leftDrawer.value = !leftDrawer.value
  }

  // 「学習完了」の横の「ゴールに戻る」：このコースが宿題に入っているゴール。無ければボタンを出さない
  // ゴールの画面から来たとき（previousPage）は、そのゴールを優先する。ゴールを使わないスクールでは問い合わせもしない
  const returnGoal = ref<{ id: number; name: string } | null>(null)
  const loadReturnGoal = async (courseId: string) => {
    // スクール設定はヘッダーが別に読み込むので、まだ届いていなければ待つ
    await new Promise<void>((resolve) => {
      if (schoolSetting.value) return resolve()
      const stop = watch(schoolSetting, (s) => { if (s) { stop(); resolve() } })
    })
    if (!schoolSetting.value?.useGoal) return
    try {
      const userGoals = await api.userGoal.index({ courseId })
      const fromGoal = (sessionStorage.getItem('previousPage') || '').match(/\/classroom\/user-goals\/(\d+)/)
      const picked = userGoals.find((ug: any) => fromGoal && String(ug.id) === fromGoal[1]) || userGoals[0]
      returnGoal.value = picked ? { id: picked.id, name: picked.goal?.name || '' } : null
    } catch (e) {
      returnGoal.value = null
    }
  }
  const goBackToGoal = () => {
    if (!returnGoal.value) return
    sessionStorage.removeItem('previousPage')
    router.push({ name: ROUTER_NAME.USER_GOAL_DETAIL, params: { userGoalId: String(returnGoal.value.id) } })
  }

  const goToHome = () => {
    const previousPage = sessionStorage.getItem('previousPage')
    if (previousPage) {
      sessionStorage.removeItem('previousPage')
      router.push(previousPage)
    } else {
      router.push({ name: ROUTER_NAME.MY_COURSES })
    }
  }

  const onResize = () => {
    isMobile.value = window.innerWidth < 600
  }

  const finishLesson = async () => {
    if (isProcessingFinish.value || !currentLesson.value) return
    try {
      isProcessingFinish.value = true
      await api.lesson.learnt({
        courseId: route.params.courseId as string,
        lessonId: currentLesson.value.lesson.id.toString()
      })
      currentLesson.value.isLearnt = true
      completedLessonCount.value++
      learningMeter.setCompletion('completed')
      learningMeter.emitLessonCompleted()
      const lessonIndex = leftBarItems.value.findIndex(
        (item) => item.id.toString() == currentLessonId.value && item.type == 'item'
      )
      if (leftBarItems.value[lessonIndex]) {
        leftBarItems.value[lessonIndex].icon = 'mdi-check'
      }
    } finally {
      isProcessingFinish.value = false
    }
  }

  const revertFinishLesson = async () => {
    if (isProcessingRevert.value || !currentLesson.value) return
    try {
      isProcessingRevert.value = true
      await api.lesson.revertLearnt({
        courseId: route.params.courseId as string,
        lessonId: currentLesson.value.lesson.id.toString()
      })
      currentLesson.value.isLearnt = false
      completedLessonCount.value--
      const lessonIndex = leftBarItems.value.findIndex(
        (item) => item.id.toString() == currentLessonId.value && item.type == 'item'
      )
      if (leftBarItems.value[lessonIndex]) {
        leftBarItems.value[lessonIndex].icon = ''
      }
    } finally {
      isProcessingRevert.value = false
    }
  }

  const upVote = async () => {
    if (!currentLesson.value || currentLesson.value.lesson.voteMark >= 1) return
    try {
      const voteForm = new VoteForm()
      voteForm.value = currentLesson.value.lesson.voteMark + 1
      voteForm.votableId = currentLesson.value.lesson.id
      voteForm.votableType = "Lesson"
      await api.vote.create(voteForm)
      currentLesson.value.lesson.voteMark = voteForm.value
      currentLesson.value.lesson.voteValues += 1
    } catch (e) { }
  }

  const downVote = async () => {
    if (!currentLesson.value || currentLesson.value.lesson.voteMark <= 0) return
    try {
      const voteForm = new VoteForm()
      voteForm.value = currentLesson.value.lesson.voteMark - 1
      voteForm.votableId = currentLesson.value.lesson.id
      voteForm.votableType = "Lesson"
      await api.vote.create(voteForm)
      currentLesson.value.lesson.voteMark = voteForm.value
      currentLesson.value.lesson.voteValues -= 1
    } catch (e) { }
  }

  const dateFormat = (date: Date) => format(date, 'YYYY/MM/DD HH:mm')

  const createChannel = () => {
    removeChannel('LessonQuestionCountChannel')
    const unlearntLessons = courseLessons.value.filter(lesson => !lesson.isLearnt).slice(0, 1)
    const lessonId = lessonIdQueryNumber.value || (unlearntLessons.length > 0 ? Number(unlearntLessons[0].lesson.id) : Number(courseLessons.value[0]?.lesson?.id))
    channel.value = makeChannel(
      'LessonQuestionCountChannel',
      async () => {
        const courseLesson = await api.courseLesson.show({
          courseId: route.params.courseId as string,
          id: lessonId,
          ...route.query
        })
        currentLessonQuestionsCount.value = courseLesson.lesson.questionsCount
      },
      () => {
        channel.value.send({
          action: 'follow',
          data: { relativeable_id: lessonId, relativeable_type: "Lesson" }
        })
      }
    )
  }

  const refetchQuestionsCount = () => {
    createChannel()
    if (currentLesson.value) {
      currentLessonQuestionsCount.value = currentLesson.value.lesson.questionsCount
    }
  }

  const initializeDefaultTab = () => {
    nextTick(() => {
      if (currentLesson.value?.lesson?.lessonType === 'live') {
        tab.value = 'LIVECHAT'
      } else if (schoolSetting.value?.useAiChatLesson) {
        tab.value = 'CHAT'
      } else {
        tab.value = 'QA'
      }
    })
  }

  const updateReview = async () => {
    try {
      review.value = await api.review.show(route.params.courseId as string)
    } catch (e) { }
  }

  const initCourse = async () => {
    const courseId = route.params.courseId as string

    // Fire every request that doesn't depend on another response up front so the
    // page loads in one roundtrip instead of a sequential waterfall.
    const coursePromise = api.course.my({ id: courseId, ...route.query })
    const courseDataPromise = Promise.all([
      api.chapter.index({ courseId, myCourse: true }),
      api.courseLesson.index({ courseId, mode: "simplify", ...route.query }),
      api.course.materials({ courseId }),
      api.banner.index({ type: "lesson" }),
      api.review.show(courseId)
    ])
    // When the lesson is already known from the URL (e.g. admin preview links),
    // its detail and materials can be fetched eagerly as well.
    loadReturnGoal(courseId)
    const eagerLessonPromise = lessonIdQueryNumber.value
      ? api.courseLesson.show({ courseId, id: lessonIdQueryNumber.value, ...route.query }).catch(() => null)
      : null
    const eagerMaterialsPromise = lessonIdQuery.value
      ? api.lesson.materials({ lessonId: lessonIdQuery.value }).catch(() => null)
      : null

    try {
      course.value = await coursePromise
    } catch (e) {
      location.href = `/courses/${courseId}`
      return
    }

    try {
      const [chaptersData, courseLessonsData, courseMaterialsData, bannersData, reviewData] = await courseDataPromise

      chapters.value = chaptersData
      courseLessons.value = courseLessonsData
      courseMaterials.value = courseMaterialsData
      banners.value = bannersData
      review.value = reviewData

      setLeftBarMenu(chapters.value, courseLessons.value)
      completedLessonCount.value = courseLessons.value.filter(item => item.isLearnt).length

      if (courseLessons.value.length > 0) {
        const unlearntLessons = courseLessons.value.filter(lesson => !lesson.isLearnt)
        const lessonId = lessonIdQueryNumber.value || (unlearntLessons.length > 0 ? Number(unlearntLessons[0].lesson.id) : Number(courseLessons.value[0].lesson.id))
        currentLesson.value = (eagerLessonPromise && await eagerLessonPromise) || await api.courseLesson.show({
          courseId,
          id: lessonId,
          ...route.query
        })
        refetchQuestionsCount()
        lessonMaterials.value = (eagerMaterialsPromise && await eagerMaterialsPromise) || await api.lesson.materials({
          lessonId: currentLesson.value.lesson.id.toString()
        })
      }

      document.addEventListener("visibilitychange", openOtherTab)

      if (lessonIdQuery.value) {
        setTimeout(() => {
          const leftBarItem = document.getElementById(`left-bar-item-${lessonIdQuery.value}`)
          if (leftBarItem) {
            leftBarItem.scrollIntoView({ behavior: "smooth", block: "center" })
          }
        }, 1000)
      }
    } catch (e) { }
  }

  return {
    // State
    mobile, course, courseLessons, currentLesson, courseMaterials, lessonMaterials,
    completedLessonCount, banners, review, chapters, isMobile, leftDrawer, rightDrawer,
    disabledNextBtn, disabledPreviousBtn, chatContent, tab, channel, currentLessonQuestionsCount,
    mobileViewMode, leftBarItems, lazyLoading, descriptionShowAll,
    // Computed
    user, schoolSetting, lessonIds, headerNavColor, headerNavTextColor, proccessCount,
    hamburgerColor, breadcrumbs, lessonIdQuery, lessonIdQueryNumber, tabQuery, currentLessonId,
    leftBarSelectedItem, headerTitle, commentable,
    // Methods
    openOtherTab, setLeftBarMenu, goNextLesson, previousLession, getBanner, goLesson,
    downloadFile, showRightMenu, toggleLeftDrawer, goToHome, onResize, finishLesson, returnGoal, goBackToGoal,
    revertFinishLesson, upVote, downVote, dateFormat, createChannel, refetchQuestionsCount,
    initializeDefaultTab, updateReview, initCourse, markdownParser,
    onVideoPlay, onVideoPause, onVideoMilestone
  }
}
